import { DEFAULT_MONEY_SCALE, roundMoney } from '@shared/money';
import type { SaleDetail, SaleDetailDiscount, SaleDetailItem } from '../domain/salesTypes';
import { formatMoney as formatPromoMoney } from '../../shared/format/public-api';

// Label por scope de descuento de venta — incluye 'manual' (canje puntos,
// descuento a mano) que PROMOTION_SCOPE_LABELS no cubre (caía a "Carrito
// completo", mislabel). Desacoplado de promotions/public-api por boundary.
const SALE_DISCOUNT_SCOPE_LABELS: Record<NonNullable<SaleDetailDiscount['scope']>, string> = {
  line: 'Por producto',
  category: 'Por categoría',
  cart: 'Carrito completo',
  manual: 'A mano en caja',
};

export type SaleDetailLineSummaryRow = {
  lineId: string;
  quantity: number;
  listTotal: number;
  discountTotal: number;
  netTotal: number;
};

export type SaleDetailDiscountSummaryRow = {
  id: string;
  promotionId: string | null;
  promotionName: string;
  discountAmount: number;
  scopeLabel: string;
  metadata: Record<string, unknown> | null;
};

function promoMinusLabel(amount: number): string {
  return `−${formatPromoMoney(amount).slice(1)}`;
}

export function summarizeSaleDetailLines(items: SaleDetailItem[]): SaleDetailLineSummaryRow[] {
  return items.map((item) => {
    const listUnitPrice = item.unitPriceOriginal ?? item.price;
    const listTotal = listUnitPrice * item.quantity;
    const discountTotal = Number(item.discountAmount ?? 0);
    const netTotal = item.price * item.quantity;
    return {
      lineId: item.id,
      quantity: item.quantity,
      listTotal,
      discountTotal,
      netTotal,
    };
  });
}

export function summarizeSaleDetailDiscounts(discounts: SaleDetailDiscount[]): SaleDetailDiscountSummaryRow[] {
  return discounts.map((discount) => ({
    id: discount.id,
    promotionId: discount.promotionId,
    promotionName: discount.label?.trim() || 'Descuento',
    discountAmount: Number(discount.discountAmount ?? 0),
    scopeLabel: SALE_DISCOUNT_SCOPE_LABELS[discount.scope ?? 'cart'] ?? 'Carrito completo',
    metadata: discount.metadata ?? null,
  }));
}

function isCartMarkerDuplicate(
  discount: SaleDetailDiscount,
  all: SaleDetailDiscount[],
): boolean {
  const meta = discount.metadata;
  if (!meta || meta.cart !== true) return false;
  const amt = Number(discount.discountAmount ?? 0);
  const promoId = discount.promotionId;
  return all.some((other) => {
    if (other === discount || other.promotionId !== promoId) return false;
    if (Number(other.discountAmount ?? 0) !== amt) return false;
    const otherMeta = other.metadata;
    return (
      typeof otherMeta?.lineId === 'string' &&
      otherMeta.lineId.length > 0 &&
      otherMeta.cart !== true
    );
  });
}

/** Agrupa filas persistidas (p. ej. cart repartido por ítem) para paridad con checkout POS. */
export function aggregateSaleDetailDiscounts(
  discounts: SaleDetailDiscount[],
): SaleDetailDiscountSummaryRow[] {
  const dedupedSource = discounts.filter((d) => !isCartMarkerDuplicate(d, discounts));
  const grouped = new Map<string, SaleDetailDiscountSummaryRow>();
  for (const row of summarizeSaleDetailDiscounts(dedupedSource)) {
    const key = row.promotionId ?? row.id;
    const prev = grouped.get(key);
    if (prev) {
      prev.discountAmount = roundMoney(prev.discountAmount + row.discountAmount, DEFAULT_MONEY_SCALE);
      if (prev.scopeLabel !== row.scopeLabel && row.scopeLabel === 'Carrito completo') {
        prev.scopeLabel = row.scopeLabel;
      }
    } else {
      grouped.set(key, { ...row });
    }
  }
  return [...grouped.values()];
}

function parseLineId(metadata: Record<string, unknown> | null): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const lineId = metadata.lineId;
  return typeof lineId === 'string' && lineId.trim().length > 0 ? lineId.trim() : null;
}

export function formatSaleDetailLinePromoHint(
  item: SaleDetailItem,
  discounts: SaleDetailDiscount[]
): string {
  const itemDiscounts = discounts.filter((discount) => {
    if (discount.promotionId && item.promotionId && discount.promotionId === item.promotionId) return true;
    const discountLineId = parseLineId(discount.metadata);
    return discountLineId != null && discountLineId === item.id;
  });

  if (itemDiscounts.length === 0) {
    const ownDiscount = Number(item.discountAmount ?? 0);
    return ownDiscount > 0 ? promoMinusLabel(ownDiscount) : '';
  }

  if (itemDiscounts.length === 1) {
    const discount = itemDiscounts[0]!;
    return `${discount.label?.trim() || 'Descuento'} · ${promoMinusLabel(Number(discount.discountAmount ?? 0))}`;
  }

  const total = itemDiscounts.reduce((sum, discount) => sum + Number(discount.discountAmount ?? 0), 0);
  return `${itemDiscounts.length} promos · ${promoMinusLabel(total)}`;
}

export function hasSaleDetailPromotions(detail: SaleDetail): boolean {
  if (Number(detail.discountTotal ?? 0) > 0) return true;
  if ((detail.discounts ?? []).length > 0) return true;
  return detail.items.some((item) => Number(item.discountAmount ?? 0) > 0 || item.promotionId != null);
}
